/**
 * Environment detection utilities. Zero dependencies — only reads process.env.
 * Extracted so instrumentation.ts and other bootstrap code can use it without
 * pulling in @/lib/utils (Mantine, Fuse, zod, etc.).
 */

/**
 * Class that represents an environment level with comparison operators.
 */
export class EnvironmentLevel {
  constructor(
    readonly name: string,
    readonly priority: number,
  ) {}

  valueOf() {
    return this.priority;
  }
}

/**
 * Map of environment names to their level objects.
 */
export const Environments = {
  PRODUCTION: new EnvironmentLevel("production", 4),
  SANDBOX: new EnvironmentLevel("sandbox", 3),
  PREVIEW: new EnvironmentLevel("preview", 2),
  LOCAL: new EnvironmentLevel("local", 1),
} as const;

/**
 * Type for valid environment names.
 */
export type Environment = keyof typeof Environments;

let envWarningEmitted = false;

/** @internal Test-only — resets module-level warning flag so tests are order-independent. */
export function _resetEnvironmentState(): void {
  envWarningEmitted = false;
}

/**
 * Gets the current environment with type safety.
 * Defaults to LOCAL when unset (fail-safe for production behaviors: crons stay off,
 * debounce uses dev timers). Unrecognized values default to PRODUCTION.
 * Deployed envs (Vercel, etc.) always set NEXT_PUBLIC_VIBRANT_ENV — see .env.example.
 */
export function getEnvironment(): EnvironmentLevel {
  const env = (process.env.NEXT_PUBLIC_VIBRANT_ENV ?? "").trim().toUpperCase();
  if (!env && !envWarningEmitted) {
    envWarningEmitted = true;
    console.warn(
      "[vibrant] NEXT_PUBLIC_VIBRANT_ENV is not set — defaulting to LOCAL. " +
        "Set NEXT_PUBLIC_VIBRANT_ENV=local in .env.local for local development.",
    );
  }
  if (!env) return Environments.LOCAL;
  const environment = env in Environments ? (env as Environment) : "PRODUCTION";
  return Environments[environment];
}

export function isProduction(): boolean {
  return getEnvironment() === Environments.PRODUCTION;
}

export function isSandbox(): boolean {
  return getEnvironment() === Environments.SANDBOX;
}

export function isLocal(): boolean {
  return getEnvironment() === Environments.LOCAL;
}

export function isPreview(): boolean {
  return getEnvironment() === Environments.PREVIEW;
}

/**
 * Whether scheduled cron jobs should run in this environment.
 * Only production and sandbox should run crons.
 *
 * Preview PR deploys get `NEXT_PUBLIC_VIBRANT_ENV=preview` from the preview-target
 * override in `terraform/live/staging/app-env/terragrunt.hcl`, so `getEnvironment()`
 * returns PREVIEW for them. The `VERCEL_TARGET_ENV` check is a second guard that does
 * not depend on that override. Don't use `VERCEL_ENV` here: it is also `"preview"` for
 * the sandbox custom environment.
 */
export function shouldRunCrons(): boolean {
  // PR preview deploys must never schedule crons (Inngest branch envs).
  if (process.env.VERCEL_TARGET_ENV === "preview") {
    return false;
  }

  const env = getEnvironment();
  return env === Environments.PRODUCTION || env === Environments.SANDBOX;
}
